export const UNIT_PRICE = 29;
// USD unit price — matches Shopify Markets AUD→USD conversion at current FX
// so the storefront price exactly equals what the US buyer sees at Shopify
// checkout. Update if the Shopify Markets rate drifts materially.
export const UNIT_PRICE_USD = 21;
// GBP unit price — set at UK retail parity (approx USD→GBP at the time of
// launch). Shopify Markets handles the actual GBP conversion at checkout;
// this is just what we display on the storefront.
export const UNIT_PRICE_GBP = 17;

// Consumer bundles at real prices. Multi-pack tiers get a visible compare-at
// price (strike-through) so the % off is loud at the moment of decision — the
// compare-at is rounded UP to the nearest $5 to look like a legitimate RRP
// rather than a math-derived number (e.g. 15% off $99 = $116.47 → shown as $120).
//
// `freeBonus` is a LIMITED-TIME promo stacked on top of the usual volume tiers:
// a 4-Pack ships with +1 free tracker, an 8-Pack with +2. The backend's
// `pricing.PACK_FREE_BONUS` must stay in sync (it adds a $0 line item to the
// Shopify draft so DSers fulfils the extra trackers automatically).
export const PACKS = [
  { id: 1, name: "Essential", label: "1 Tracksy", price: 29, priceUsd: 21, priceGbp: 17, tagline: "One vehicle or asset" },
  { id: 2, name: "Popular", label: "2 Tracksy", price: 55, priceUsd: 40, priceGbp: 32, tagline: "Car + trailer, or two assets" },
  { id: 4, name: "Best Value", label: "4 Tracksy", price: 99, priceUsd: 71, priceGbp: 56, compareAt: 120, compareAtUsd: 85, compareAtGbp: 68, discountPct: 15, freeBonus: 1, tagline: "Vehicles, tools and gear", recommended: true },
  { id: 8, name: "Multi-Asset", label: "8 Tracksy", price: 189, priceUsd: 136, priceGbp: 107, compareAt: 240, compareAtUsd: 170, compareAtGbp: 134, discountPct: 20, freeBonus: 2, tagline: "Family, farm or workshop" },
];

// Site-wide promo banner config — reads from the same source as PACKS so the
// headline numbers can never drift from what shoppers actually get.
export const LIMITED_TIME_PROMO = {
  enabled: true,
  tagline: "Limited time: free bonus trackers",
  // Expressed as [{packId, free}]. UI renders this verbatim so if the deal
  // changes you only edit it here and in pricing.PACK_FREE_BONUS on the backend.
  bonuses: [{ packId: 4, free: 1 }, { packId: 8, free: 2 }],
};

// Markets Tracksy currently fulfils to. `supportedPlatforms` is a hard gate: a shopper
// in that country can only check out a pack whose platform is listed here (prevents AU
// supplier getting US orders they can't ship fast). Add platforms once DSers mapping
// is confirmed for each one.
export const MARKETS = {
  AU: {
    code: "AU",
    name: "Australia",
    currency: "AUD",
    symbol: "$",
    flag: "🇦🇺",
    supportedPlatforms: ["apple", "android"],
    shippingCopy: "Free express Australia-wide",
    shippingShort: "Free AU shipping",
  },
  US: {
    code: "US",
    name: "United States",
    currency: "USD",
    symbol: "$",
    flag: "🇺🇸",
    supportedPlatforms: ["apple", "android"],  // US supplier fulfils both lines in DSers.
    shippingCopy: "Free standard shipping US-wide",
    shippingShort: "Free US shipping",
  },
  GB: {
    code: "GB",
    name: "United Kingdom",
    currency: "GBP",
    symbol: "£",
    flag: "🇬🇧",
    supportedPlatforms: ["apple", "android"],
    shippingCopy: "Free UK-wide shipping",
    shippingShort: "Free UK shipping",
  },
};

export const marketFor = (code) => MARKETS[code] || MARKETS.AU;
export const packPrice = (pack, market) => {
  const cur = market?.currency;
  if (cur === "USD") return pack.priceUsd;
  if (cur === "GBP") return pack.priceGbp;
  return pack.price;
};
export const packCompareAt = (pack, market) => {
  const cur = market?.currency;
  if (cur === "USD") return pack.compareAtUsd;
  if (cur === "GBP") return pack.compareAtGbp;
  return pack.compareAt;
};
export const unitPriceFor = (market) => {
  const cur = market?.currency;
  if (cur === "USD") return UNIT_PRICE_USD;
  if (cur === "GBP") return UNIT_PRICE_GBP;
  return UNIT_PRICE;
};

export const FLEET_TIERS = [
  { id: "small", range: "3–10 devices", name: "Small Fleet", selfServe: true, desc: "Tradies, sole operators and small teams equipping their first vehicles and trailers. Buy online in any quantity." },
  { id: "growing", range: "10–25 devices", name: "Growing Fleet", selfServe: true, desc: "Growing businesses adding trackers across vehicles, tools and equipment. Mix packs and quantities at checkout." },
  { id: "business", range: "25–100 devices", name: "Business Fleet", selfServe: true, desc: "Established operators standardising tracking across a whole fleet. Order online, no enquiry needed." },
  { id: "enterprise", range: "100+ devices", name: "Enterprise / Bulk", selfServe: false, desc: "Large deployments, resellers and multi-site organisations — request tailored pricing and a purchase-order process." },
];

export const ENQUIRY_RANGES = ["100–250 devices", "250–500 devices", "500–1,000 devices", "1,000+ devices"];
export const MAX_QTY = 999;

// Volume rewards ladder — based on total trackers in the order (pack size × quantity, across the cart).
// Edit percentages / perks here; everything on the site reads from this list.
export const VOLUME_TIERS = [
  { id: "crew", min: 10, name: "Crew", discount: 0.05, headline: "5% off", freeTrackers: 2, perks: ["2 free Tracksy trackers", "Priority email support"] },
  { id: "fleet", min: 25, name: "Fleet", discount: 0.08, headline: "8% off", freeTrackers: 3, perks: ["3 free Tracksy trackers", "Priority support — same-day replies", "Extended 24-month warranty"] },
  { id: "fleetpro", min: 50, name: "Fleet Pro", discount: 0.1, headline: "10% off", freeTrackers: 5, perks: ["5 free Tracksy trackers", "VIP support line with a named contact", "24-month warranty + advance replacement"] },
  { id: "enterprise", min: 100, name: "Enterprise", discount: 0.12, headline: "12% off", freeTrackers: 8, perks: ["8 free Tracksy trackers", "Dedicated account manager", "36-month warranty + advance replacement", "Or request tailored pricing"] },
];

export const volumeTierFor = (count) => [...VOLUME_TIERS].reverse().find((t) => count >= t.min) || null;
export const nextVolumeTier = (count) => VOLUME_TIERS.find((t) => count < t.min) || null;

export const savingsVsSingles = (pack) => pack.id * UNIT_PRICE - pack.price;

export const COLORS = [
  { id: "obsidian", name: "Obsidian Black", hex: "#1D1D1F", availableIn: [1, 2, 4, 8] },
  { id: "ceramic", name: "Ceramic White", hex: "#F2F2ED", availableIn: [] },
];

export const isColorAvailable = (color, packId) => color.availableIn.includes(packId);

export const FREE_SHIPPING_THRESHOLD = 50;

export const IMAGES = {
  heroBlack: "/images/hero-black.jpg",
  heroBlackAndroid: "/images/hero-black-android.webp",
  heroUte: "/images/hero-ute.jpg",
  team: "/images/tracksy-team.webp",
  exploded: "/images/exploded.jpg",
  explodedAndroid: "/images/exploded-android.webp",
  whiteDuo: "/images/white-duo.jpg",
  keys: "/images/keys.jpg",
  bike: "/images/bike.jpg",
  eightPack: "/images/eight-pack.jpg",
  eightPackAndroid: "/images/eight-pack-android.webp",
  eightPackWhite: "/images/eight-pack-white.jpg",
  luggage: "/images/luggage.jpg",
  puckMacro: "/images/puck-macro.webp",
  hand: "/images/tracker-phone-hero.webp",
  handAndroid: "/images/tracker-phone-hero-android.webp",
  outdoor: "/images/outdoor-gear.jpg",
  generator: "/images/generator.jpg",
  trailer: "/images/trailer.jpg",
  toolbox: "/images/toolbox.jpg",
  fleetVans: "/images/fleet-vans.jpg",
  caravan: "/images/caravan.jpg",
  motorbike: "/images/motorbike.jpg",
  carBoot: "/images/car-boot.jpg",
  hiddenEngine: "/images/hidden-engine.jpg",
  hiddenDash: "/images/hidden-dash.jpg",
  hiddenBumper: "/images/hidden-bumper.jpg",
  hiddenSeat: "/images/hidden-seat.jpg",
  hiddenTrailer: "/images/hidden-trailer.jpg",
  hiddenSpare: "/images/hidden-spare.jpg",
  // Clean "N trackers" composite shots used on the Bundle selector.
  // Pack-{n}.webp = Apple theme (cream), pack-{n}-android.webp = Android (blue paper).
  pack1: "/images/pack-1.webp",
  pack2: "/images/pack-2.webp",
  pack4: "/images/pack-4.webp",
  pack8: "/images/pack-8.webp",
  pack1Android: "/images/pack-1-android.webp",
  pack2Android: "/images/pack-2-android.webp",
  pack4Android: "/images/pack-4-android.webp",
  pack8Android: "/images/pack-8-android.webp",
};

export const packImage = (colorId, packId, platform = "apple") => {
  if (colorId === "ceramic") return packId === 8 ? IMAGES.eightPackWhite : IMAGES.whiteDuo;
  const android = platform === "android";
  if (packId === 1) return android ? IMAGES.pack1Android : IMAGES.pack1;
  if (packId === 2) return android ? IMAGES.pack2Android : IMAGES.pack2;
  if (packId === 4) return android ? IMAGES.pack4Android : IMAGES.pack4;
  if (packId === 8) return android ? IMAGES.pack8Android : IMAGES.pack8;
  return android ? IMAGES.pack1Android : IMAGES.pack1;
};
